import React from "react";
import { AlertTriangle, LogOut, Trash2, X } from "lucide-react";
import { createPortal } from "react-dom";

const variantStyles = {
  danger: {
    icon: Trash2,
    iconWrapper:
      "bg-red-50 text-red-500 dark:bg-red-500/10 dark:text-red-400",
    button:
      "bg-red-500 hover:bg-red-600 shadow-[0_3px_10px_rgba(239,68,68,0.16)] hover:shadow-[0_5px_14px_rgba(239,68,68,0.22)]",
  },

  logout: {
    icon: LogOut,
    iconWrapper:
      "bg-red-50 text-red-500 dark:bg-red-500/10 dark:text-red-400",
    button:
      "bg-red-500 hover:bg-red-600 shadow-[0_3px_10px_rgba(239,68,68,0.16)] hover:shadow-[0_5px_14px_rgba(239,68,68,0.22)]",
  },

  warning: {
    icon: AlertTriangle,
    iconWrapper:
      "bg-amber-50 text-amber-500 dark:bg-amber-500/10 dark:text-amber-400",
    button:
      "bg-amber-500 hover:bg-amber-600 shadow-[0_3px_10px_rgba(245,158,11,0.16)] hover:shadow-[0_5px_14px_rgba(245,158,11,0.22)]",
  },
};

export default function ConfirmationModal({open,title = "Are you sure?",description = "This action cannot be undone.",confirmText = "Confirm",cancelText = "Cancel",variant = "danger",icon,loading = false,onConfirm,onCancel,}) {
  if (!open) return null;

  const styles = variantStyles[variant] || variantStyles.danger;
  const Icon = icon || styles.icon;

  const modal = (
    <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-slate-900/25 px-4 backdrop-blur-[3px] dark:bg-black/55"
      onMouseDown={(event) => {
        if (
          event.target === event.currentTarget &&
          !loading
        ) {
          onCancel?.();
        }
      }}
    >
      <div className="w-full max-w-sm overflow-hidden rounded-2xl border border-[#e3edf3] bg-white shadow-[0_20px_60px_rgba(15,23,42,0.14)] dark:border-[#2a3b4a] dark:bg-[#162330] dark:shadow-[0_20px_60px_rgba(0,0,0,0.4)]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-[#edf2f5] px-5 py-4 dark:border-[#293b4a]">
          <div className="flex items-start gap-3">
             <div
              className={[
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl",
                styles.iconWrapper,
              ].join(" ")}
            >
              <Icon
                size={17}
                strokeWidth={1.8}
              />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#172b3a] dark:text-[#f1f6f9]">
                {title}
              </h3>

              <p className="mt-1 text-xs leading-5 text-[#8293a0] dark:text-[#91a3b1]">
                {description}
              </p>
            </div>
          </div>

          <button
            type="button"
            disabled={loading}
            onClick={onCancel}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[#94a3af] transition hover:bg-[#edf8fc] hover:text-[#147db5] disabled:opacity-40 dark:text-[#8295a5] dark:hover:bg-[#1d3445] dark:hover:text-[#69c7f1]"
          >
            <X size={15} />
          </button>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4">
          <button
            type="button"
            disabled={loading}
            onClick={onCancel}
            className="rounded-lg border border-[#dce8ee] bg-white px-4 py-2 text-xs font-semibold text-[#647684] transition hover:border-[#b9ddeb] hover:bg-[#f3faff] hover:text-[#147db5] disabled:opacity-50 dark:border-[#304452] dark:bg-[#162330] dark:text-[#a4b4c0] dark:hover:border-[#3d6075] dark:hover:bg-[#1b3344] dark:hover:text-[#69c7f1]"
          >
            {cancelText}
          </button>

          <button
            type="button"
            disabled={loading}
            onClick={onConfirm}
            className={[
              "flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-semibold text-white transition",
              styles.button,
            ].join(" ")}
          >
            {loading ? (
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            ) : (
              <Icon size={14} />
            )}

            {loading ? "Please wait..." : confirmText}
          </button>

        </div>
      </div>
    </div>
  );

  return createPortal(modal,document.body);
}